/**
 * The role table of ADR-001 §6.2 as a code artefact. The same object drives
 * (a) `capabilities` and `menu` in GET /me, (b) the scope the data layer requires, (c) the test matrix.
 * Adding a role = one key here + one RLS policy branch. No person appears anywhere in this file.
 */
import { CAPABILITIES, GRANTABLE_CAPABILITIES } from '@amplispot/contracts';
export const POLICY = {
    founder: {
        clients: 'all', tasks: 'all', timesheets: 'all', usage: 'all', revenue: true, settings: true, team: true,
        money: true, decide: 'view', checklists: 'any', requests: 'triage', changeSpoc: true, reassign: true, toneReason: true,
        capture: true, ideas: 'decide', filesTimesheet: false, timesheetGate: false,
        timesheetAnalysis: true, managerFeedback: true, myDay: 'none',
        holidays: false, systemAdmin: true, actAs: true,
    },
    ops_ad: {
        clients: 'all', tasks: 'all', timesheets: 'all', usage: 'all', revenue: true, settings: true, team: true,
        money: true, decide: 'decide', checklists: 'any', requests: 'triage', changeSpoc: true, reassign: true, toneReason: true,
        capture: true, ideas: 'decide', filesTimesheet: true, timesheetGate: false,
        timesheetAnalysis: false, managerFeedback: true, myDay: 'plan',
        holidays: false, systemAdmin: false, actAs: true,
    },
    ops_member: {
        clients: 'own', tasks: 'own', timesheets: 'own', usage: 'own', revenue: false, settings: false, team: false,
        money: false, decide: 'raise', checklists: 'own', requests: 'none', changeSpoc: false, reassign: false, toneReason: false,
        capture: true, ideas: 'submit', filesTimesheet: true, timesheetGate: true,
        timesheetAnalysis: false, managerFeedback: false, myDay: 'full',
        holidays: false, systemAdmin: false, actAs: false,
    },
    sales: {
        clients: 'none', tasks: 'none', timesheets: 'none', usage: 'none', revenue: false, settings: false, team: false,
        money: false, decide: 'none', checklists: 'none', requests: 'raise_own', changeSpoc: false, reassign: false, toneReason: false,
        capture: false, ideas: 'submit', filesTimesheet: false, timesheetGate: false,
        timesheetAnalysis: false, managerFeedback: false, myDay: 'none',
        holidays: false, systemAdmin: false, actAs: false,
    },
    marketing: {
        clients: 'none', tasks: 'none', timesheets: 'none', usage: 'none', revenue: false, settings: false, team: false,
        money: false, decide: 'none', checklists: 'none', requests: 'raise_own', changeSpoc: false, reassign: false, toneReason: false,
        capture: false, ideas: 'submit', filesTimesheet: false, timesheetGate: false,
        timesheetAnalysis: false, managerFeedback: false, myDay: 'none',
        holidays: false, systemAdmin: false, actAs: false,
    },
    /** HR: sees no client, task, timesheet, usage or revenue data; owns the holiday calendar (built in step 4). */
    hr: {
        clients: 'none', tasks: 'none', timesheets: 'none', usage: 'none', revenue: false, settings: false, team: false,
        money: false, decide: 'none', checklists: 'none', requests: 'none', changeSpoc: false, reassign: false, toneReason: false,
        capture: false, ideas: 'none', filesTimesheet: false, timesheetGate: false,
        timesheetAnalysis: false, managerFeedback: false, myDay: 'none',
        holidays: true, systemAdmin: false, actAs: false,
    },
};
export const isManager = (role) => POLICY[role].clients === 'all' && POLICY[role].settings;
/** The capability set for a role, derived only from POLICY. Order follows CAPABILITIES. */
export function capabilitiesFor(role) {
    const p = POLICY[role];
    const has = new Set();
    const add = (...c) => c.forEach((x) => has.add(x));
    if (p.clients === 'all')
        add('today.company', 'clients.read.all', 'clients.create', 'clients.edit.all', 'client.emailDomains.write');
    if (p.clients === 'own')
        add('today.own', 'clients.read.own', 'clients.edit.own');
    if (p.tasks === 'all')
        add('tasks.read.all');
    // Operator decision (t-b5f61594): the founder, like the AD, gives work to someone else directly. Deciding
    // change requests and checking proof stay the AD's.
    if (p.reassign)
        add('task.reassign');
    if (p.tasks === 'own')
        add('tasks.read.own');
    if (p.usage === 'all')
        add('usage.read.all', 'usage.upload');
    if (p.usage === 'own')
        add('usage.read.own');
    if (p.timesheets === 'all')
        add('timesheet.read.all');
    if (p.timesheets === 'own')
        add('timesheet.read.own');
    if (p.filesTimesheet)
        add('timesheet.file');
    if (p.timesheetGate)
        add('timesheet.gated');
    if (p.timesheetAnalysis)
        add('timesheet.analysis.read');
    if (p.managerFeedback)
        add('timesheet.feedback.write');
    // Operator decision 12: the founder checks proof on closures made by the associate director.
    if (p.decide === 'view')
        add('proof.check.ad_closures');
    if (p.revenue)
        add('revenue.read');
    if (p.team)
        add('team.read');
    if (p.money)
        add('money.read');
    if (p.settings)
        add('settings.write', 'users.read', 'users.write', 'audit.read');
    if (p.decide === 'view')
        add('changeRequest.view');
    if (p.decide === 'decide')
        add('changeRequest.view', 'changeRequest.decide', 'proof.check');
    if (p.decide === 'raise')
        add('changeRequest.raise');
    if (p.checklists === 'any')
        add('checklist.write.any');
    if (p.checklists === 'own')
        add('checklist.write.own');
    if (p.requests === 'raise_own')
        add('requests.raise', 'requests.read.own');
    if (p.requests === 'triage')
        add('requests.triage');
    if (p.changeSpoc)
        add('client.changeSpoc');
    if (p.toneReason)
        add('toneReason.read');
    if (p.capture)
        add('capture.use');
    if (p.holidays)
        add('holidays.manage');
    if (p.systemAdmin)
        add('settings.system');
    if (p.actAs)
        add('actAs.member');
    if (p.ideas === 'submit')
        add('ideas.submit');
    if (p.ideas === 'decide')
        add('ideas.submit', 'ideas.decide');
    return CAPABILITIES.filter((c) => has.has(c));
}
/**
 * What one person holds: their role's capabilities plus the capabilities granted to them personally
 * (operator decisions 11–12: `holidays.manage`, ADR-005's hr.admin, is carried by the hr role and grantable to
 * anyone). Only GRANTABLE_CAPABILITIES count from `grants`, so a stray value can never widen access.
 */
export function capabilitiesOf(role, grants = []) {
    const base = new Set(capabilitiesFor(role));
    for (const g of grants)
        if (GRANTABLE_CAPABILITIES.includes(g))
            base.add(g);
    return CAPABILITIES.filter((c) => base.has(c));
}
//# sourceMappingURL=policy.js.map